import mediaQuery from 'css-mediaquery';

import { createComponents } from './create-components';
import { createCustomMedia } from './create-custom-media';
import { createGlobals } from './create-globals';
import { createMixins } from './create-mixins';
import { createPalette } from './create-palette';
import { createShadows } from './create-shadows';
import { createTokens } from './create-tokens';
import { createTypography } from './create-typography';

/**
 * @typedef {object} Breakpoints
 * @property {object} values
 * @property {number} values.xs
 * @property {number} values.sm
 * @property {number} values.md
 * @property {number} values.lg
 * @property {number} values.xl
 */

/**
 * @typedef {object} BaseVanSiteTheme
 * @property {Breakpoints} breakpoints
 * @property {import('@mui/material/styles').Components} components
 * @property {import('@mui/material/styles').TypographyStyle} typography
 * @property {import('@mui/material/styles').Palette & {neutral: { [key: string]: string}}} palette
 * @property {import('@mui/material/styles').Shadows} shadows
 * @property {'ltr'} direction
 * @property {object} shape
 * @property {number} shape.borderRadius
 * @property {import('./create-globals').MuiThemeGlobals} globals
 * @property {Record<string,string>} tokens - Sharetribe design tokens (see create-tokens.js)
 * @property {Record<string,object>} mixins - Sharetribe @apply property sets as sx objects
 * @property {{viewportLargeWithPaddings: string, viewportXLarge: string}} customMedia
 */

/**
 * @param {{direction?: 'ltr' | 'rtl', deviceType?: 'mobile' | 'desktop'}} config
 * @returns {import('@mui/material/styles').ThemeOptions & BaseVanSiteTheme}
 */
const createBaseOptions = (config) => {
  const { direction = 'ltr', deviceType = 'desktop' } = config;

  const tokens = createTokens();

  const ssrMatchMedia = (query) => ({
    matches: mediaQuery.match(query, {
      // The estimated CSS width of the browser.
      width: deviceType === 'mobile' ? '0px' : '1200px',
    }),
  });

  return {
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1440,
      },
    },
    components: createComponents(ssrMatchMedia),
    typography: createTypography(),
    palette: createPalette(),
    shadows: createShadows(),
    direction,
    shape: { borderRadius: 8 },
    globals: createGlobals(),
    tokens,
    mixins: createMixins(tokens),
    customMedia: createCustomMedia(),
  };
};

export default createBaseOptions;
